<script setup lang="ts">
import { computed } from 'vue';
import { marked } from 'marked';
// @ts-ignore
import defaultOgImageUrl from '~/assets/jpg/default-og-image.jpg?url';
import type { SbBlokData } from '@storyblok/vue';
import { addPinterestDataAttributes } from '~/utils/pinterest';
import { storyblokImage, transformStoryblokImagesInHtml } from '~/utils/storyblok-image';

interface Blok {
    _uid: string;
    content: string;
    image?: {
        filename: string;
    };
    title: string;
    additionalBlocks?: SbBlokData[];
    published_at?: string;
    first_published_at?: string;
    created_at: string;
}

const props = withDefaults(
    defineProps<{
        blok: Blok;
    }>(),
    {
        blok: () =>
            ({
                _uid: '',
                content: '',
                title: '',
                additionalBlocks: [],
                created_at: new Date().toISOString(),
            }) as Blok,
    },
);

const date = new Date(
    props.blok.first_published_at || props.blok.published_at || props.blok.created_at,
);

const articleRendered = computed(() => {
    // Transform the markdown content to HTML using marked
    return marked.parse(props.blok.content, {
        gfm: true,
        breaks: true,
    });
});

const requestUrl = useRequestURL();
const siteOrigin = (requestUrl?.origin ?? 'https://tiptapp.com').replace(/\/$/, '');
const route = useRoute();
const articleUrl = computed(() => `${siteOrigin}${route.path}`);

const articleHtmlWithPinterest = computed(() =>
    addPinterestDataAttributes(
        transformStoryblokImagesInHtml(articleRendered.value ?? '', { width: 1536 }),
        {
            articleUrl: articleUrl.value,
            defaultDescription: props.blok.title,
        },
    ),
);

const ogImage = computed(
    () => storyblokImage(props.blok.image?.filename, { width: 1200 }) || defaultOgImageUrl,
);
const ogDescription = computed(() => props.blok.title || '');

useSeoMeta({
    // @ts-ignore ogType doesn't seem to be supported by the type but it still adds the correct tag
    ogType: 'blog',
    ogImage: () => ogImage.value,
    ogDescription: () => ogDescription.value,
    twitterDescription: () => ogDescription.value,
});
</script>

<template>
    <main class="pb-16 lg:pb-24 bg-white dark:bg-midnight-blue antialiased">
        <section
            ref="root"
            :class="`relative bg-no-repeat bg-cover bg-center bg-gray-300 bg-blend-multiply`"
            :style="`background-image: url('${storyblokImage(blok.image?.filename, {
                width: 1600,
            })}')`"
        >
            <div class="absolute inset-0" style="mix-blend-mode: multiply"></div>
            <div
                class="bg-gradient-to-t from-[rgba(0,0,0,0.8)] bg-blend-multiply h-[500px] relative flex items-center justify-center"
            >
                <div class="absolute max-w-3xl bottom-0 mx-auto pb-8 px-8 text-center">
                    <h1 class="mb-4 text-5xl leading-none text-white font-bold">
                        {{ blok.title }}
                    </h1>
                    <p class="mb-8 text-lg font-normal text-white lg:text-xl">
                        {{ blok.published_at ? $t('blog.published') : $t('blog.created') }}
                        {{
                            $d(date, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            })
                        }}
                    </p>
                </div>
            </div>
        </section>

        <div class="flex justify-between mt-12 px-4 mx-auto max-w-screen-xl">
            <article
                class="mx-auto w-full max-w-3xl format format-sm sm:format-base lg:format-lg format-blue dark:format-invert"
            >
                <ClientOnly>
                    <!-- Render the HTML produced by marked from markdown content -->
                    <div v-html="articleHtmlWithPinterest"></div>
                </ClientOnly>
            </article>
        </div>
    </main>

    <aside v-for="additionalBlok in props.blok.additionalBlocks">
        <StoryblokComponent
            v-if="additionalBlok.component === 'featured-articles-grid'"
            :blok="{
                ...additionalBlok,
                // Just to make sure we don't include the current article in any related articles
                excludeIds: [blok._uid],
            }"
        />
        <StoryblokComponent v-else :blok="additionalBlok" />
    </aside>
</template>
